import { createBrowserRouter, redirect, LoaderFunctionArgs } from 'react-router-dom';
import { authApi, projectsApi } from '@/utils/api';
import App from './App';
import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import EditorPage from '@/pages/EditorPage';
import ErrorPage from '@/components/ErrorPage';

// Auth helper functions
const checkAuthStatus = async () => {
  try {
    const response = await authApi.me();
    if (response.data.success) {
      return response.data.data;
    }
    return null;
  } catch (error) {
    return null;
  }
};

const requireAuth = async () => {
  const user = await checkAuthStatus();
  if (!user) {
    throw redirect('/login');
  }
  return user;
};

const requireGuest = async () => {
  const user = await checkAuthStatus();
  if (user) {
    throw redirect('/dashboard');
  }
  return null;
};

// Loaders
const dashboardLoader = async () => {
  const user = await requireAuth();
  
  try {
    const response = await projectsApi.getAll();
    if (response.data.success) {
      return {
        user,
        projects: response.data.data
      };
    }
    throw new Error(response.data.error || 'Failed to load projects');
  } catch (error: any) {
    throw new Error(error.response?.data?.error || error.message || 'Failed to load projects');
  }
};

const editorLoader = async ({ params }: LoaderFunctionArgs) => {
  const user = await requireAuth();
  const projectId = params.projectId;
  
  if (!projectId) {
    throw new Error('Project ID is required');
  }

  try {
    // Load project details
    const projectResponse = await projectsApi.getById(parseInt(projectId));
    if (!projectResponse.data.success) {
      throw new Error(projectResponse.data.error || 'Failed to load project');
    }
    
    return {
      user,
      project: projectResponse.data.data
    };
  } catch (error: any) {
    throw new Error(error.response?.data?.error || error.message || 'Failed to load project');
  }
};

const loginLoader = async () => {
  await requireGuest();
  return null;
};

// Actions
const loginAction = async ({ request }: { request: Request }) => {
  const formData = await request.formData();
  const email = formData.get('email') as string;
  const password = formData.get('password') as string;

  if (!email || !password) {
    return {
      error: 'Email and password are required'
    };
  }

  try {
    const response = await authApi.login(email, password);
    if (response.data.success) {
      return redirect('/dashboard');
    }
    return {
      error: response.data.error || 'Login failed'
    };
  } catch (error: any) {
    return {
      error: error.response?.data?.error || error.message || 'Login failed'
    };
  }
};

const createProjectAction = async ({ request }: { request: Request }) => {
  await requireAuth();
  
  const formData = await request.formData();
  const name = formData.get('name') as string;

  if (!name?.trim()) {
    return {
      error: 'Project name is required'
    };
  }

  try {
    const response = await projectsApi.create(name.trim());
    if (response.data.success) {
      return redirect(`/editor/${response.data.data.id}`);
    }
    return {
      error: response.data.error || 'Failed to create project'
    };
  } catch (error: any) {
    return {
      error: error.response?.data?.error || error.message || 'Failed to create project'
    };
  }
};

const deleteProjectAction = async ({ request }: { request: Request }) => {
  await requireAuth();
  
  const formData = await request.formData();
  const projectId = formData.get('projectId') as string;

  if (!projectId) {
    return {
      error: 'Project ID is required'
    };
  }

  try {
    const response = await projectsApi.delete(parseInt(projectId));
    if (response.data.success) {
      return { success: true };
    }
    return {
      error: response.data.error || 'Failed to delete project'
    };
  } catch (error: any) {
    return {
      error: error.response?.data?.error || error.message || 'Failed to delete project'
    };
  }
};

// Router configuration
export const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        loader: async () => redirect('/dashboard')
      },
      {
        path: "login",
        element: <LoginPage />,
        loader: loginLoader,
        action: loginAction,
        errorElement: <ErrorPage />
      },
      {
        path: "dashboard",
        element: <DashboardPage />,
        loader: dashboardLoader,
        action: createProjectAction,
        errorElement: <ErrorPage />
      },
      {
        path: "editor/:projectId",
        element: <EditorPage />,
        loader: editorLoader,
        errorElement: <ErrorPage />
      },
      {
        path: "projects/:projectId/delete",
        action: deleteProjectAction,
        errorElement: <ErrorPage />
      }
    ]
  },
  {
    path: "*",
    loader: async () => redirect('/dashboard')
  }
]);